import React, { Dispatch, SetStateAction } from 'react'
import styled from '@emotion/styled'
import { OrderInfo } from './type'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Button from 'antd/lib/button'
import {
  PendingCTA,
  AcceptedCTA,
  FulfilledCTA,
  RefundedCTA,
  DeliveryStatusCTA,
  PickupStatusCTA
} from './CallToAction'
import { getIcon } from 'components/Orders/Info/utils'
import UserFlow from 'components/Utils/Userflow'
import { OrderPaymentStatusTag } from '../OrderStatuses'

interface OrderActionsType {
  order: Partial<OrderInfo>
  successCallback: () => void
  setShowRefundForm: Dispatch<SetStateAction<boolean>>
}

const OrderActions = ({
  order,
  successCallback,
  setShowRefundForm
}: OrderActionsType) => {
  const { transaction_id, status } = order
  const { userflow } = UserFlow()

  const print = (orderId: string) => {
    try {
      // Only manipulate Userflow if it's properly initialized
      if (userflow.isIdentified()) {
        userflow.remount()
        userflow.setBaseZIndex(0)
      }

      const url = `${window.location.origin}/print-order/${orderId}`
      const printPage = window.open(url, '_blank')

      // Restore Userflow state after opening print window
      if (userflow.isIdentified()) {
        userflow.remount()
        userflow.setBaseZIndex(99999)
      }

      if (printPage) printPage.onafterprint = printPage.close
    } catch (error) {
      console.error('Error in print function:', error)
      // Fallback: just open the print page without Userflow manipulation
      const url = `${window.location.origin}/print-order/${orderId}`
      const printPage = window.open(url, '_blank')
      if (printPage) printPage.onafterprint = printPage.close
    }
  }

  return (
    <OrderActionsContainer>
      <Col span={24}>
        <Row
          justify='space-between'
          align='middle'
          className='order-actions'
          data-testid='order-info-actions'
        >
          <span
            className='transaction'
            data-testid='order-info-actions-transaction-id'
          >
            Order
            <span className='id' data-testid='order-details-transaction-no'>
              #{transaction_id}
            </span>
            {order.status !== 'refunded' &&
              order.payment_status === 'partially_refunded' &&
              OrderPaymentStatusTag(order.payment_status)}
          </span>
          <Row className='_mb-0' align='middle'>
            <Row className='_mb-0 _mr-8' justify='end' align='middle'>
              <Col>
                <Button
                  data-testid='order-info-actions-print'
                  onClick={() => print(order.transaction_id || '')}
                  type='primary'
                  ghost
                  icon={getIcon('printer')}
                >
                  Print
                </Button>
              </Col>
            </Row>

            {status === 'pending' && (
              <PendingCTA
                order={order as OrderInfo}
                successCallback={successCallback}
              />
            )}
            {status === 'accepted' && (
              <AcceptedCTA
                order={order as OrderInfo}
                successCallback={successCallback}
                setShowRefundForm={setShowRefundForm}
              />
            )}
            {status === 'fulfilled' && (
              <FulfilledCTA
                order={order as OrderInfo}
                setShowRefundForm={setShowRefundForm}
              />
            )}
            {status === 'refunded' && (
              <RefundedCTA
                order={order as OrderInfo}
                successCallback={successCallback}
                setShowRefundForm={setShowRefundForm}
              />
            )}
          </Row>
        </Row>
        <DeliveryStatusCTA
          order={order as OrderInfo}
          successCallback={successCallback}
        />
        <PickupStatusCTA
          order={order as OrderInfo}
          successCallback={successCallback}
        />
      </Col>
    </OrderActionsContainer>
  )
}

const OrderActionsContainer = styled(Row)`
  background: #4a4a68;
  padding: 16px;

  .order-actions {
    margin-bottom: 0;
  }

  .transaction {
    color: #fff;
    font-size: 24px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .transaction > .id {
    color: #d092dd;
  }

  .delivery-status {
    font-weight: 500;
    font-size: 18px;
    color: #fff;
  }
`

export default OrderActions
